import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { label: "Community", href: "#community" },
  { label: "Articles", href: "#articles" },
  { label: "Shop", href: "#shop" },
  { label: "Creators", href: "#creators" },
];

export default function Navbar({ onAbout, onFAQ, onGetApp, onContact }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <nav
      data-testid="navbar"
      className={`sticky top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a href="#" data-testid="nav-logo" className="flex items-center gap-2 select-none">
          <div className="w-8 h-8 flex-shrink-0">
            <img
              src="/fanible-icon.png"
              alt="Fanible icon"
              className="w-full h-full object-cover rounded-xl"
              style={{ mixBlendMode: "screen" }}
            />
          </div>
          <span className="font-outfit font-black text-xl md:text-2xl tracking-tighter text-white uppercase">
            Fanible
          </span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-6">
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-sm text-[#A1A1AA] hover:text-white transition-colors duration-100 font-medium tracking-wide"
            >
              {link.label}
            </a>
          ))}
          <button
            onClick={onAbout}
            data-testid="nav-about-btn"
            className="text-sm text-[#A1A1AA] hover:text-white transition-colors duration-100 font-medium tracking-wide"
          >
            About
          </button>
          <button
            onClick={onFAQ}
            data-testid="nav-faq-btn"
            className="text-sm text-[#A1A1AA] hover:text-white transition-colors duration-100 font-medium tracking-wide"
          >
            FAQ
          </button>
          <div className="w-px h-4 bg-white/15" />
          <button
            onClick={onContact}
            data-testid="nav-contact-btn"
            className="text-sm text-[#A1A1AA] hover:text-white transition-colors duration-100 font-medium tracking-wide"
          >
            Contact
          </button>
        </div>

        {/* Download CTA */}
        <button
          data-testid="nav-download-btn"
          onClick={onGetApp}
          className="hidden md:inline-flex items-center gap-2 bg-[#3B9EFF] text-black font-bold text-sm uppercase tracking-wider rounded-full px-6 py-2.5 hover:bg-white transition-colors duration-150 hover:scale-[1.03] active:scale-[0.97]"
        >
          Pre-Register
        </button>

        {/* Mobile Toggle */}
        <button
          data-testid="nav-mobile-menu-btn"
          className="md:hidden text-white p-1"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-black/95 backdrop-blur-xl border-b border-white/10 px-6 overflow-hidden"
          >
            <div className="py-4 space-y-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="block py-3 text-[#A1A1AA] hover:text-white transition-colors font-medium border-b border-white/5"
                >
                  {link.label}
                </a>
              ))}
              <button
                onClick={() => { setMenuOpen(false); onAbout(); }}
                className="block w-full text-left py-3 text-[#A1A1AA] hover:text-white transition-colors font-medium border-b border-white/5"
              >
                About Us
              </button>
              <button
                onClick={() => { setMenuOpen(false); onFAQ(); }}
                className="block w-full text-left py-3 text-[#A1A1AA] hover:text-white transition-colors font-medium border-b border-white/5"
              >
                FAQ
              </button>
              <button
                onClick={() => { setMenuOpen(false); onContact(); }}
                className="block w-full text-left py-3 text-[#A1A1AA] hover:text-white transition-colors font-medium border-b border-white/5"
              >
                Get in Touch
              </button>
              <a href="/privacy" className="block py-3 text-[#A1A1AA] hover:text-white transition-colors font-medium border-b border-white/5 text-sm">Privacy Policy</a>
              <a href="/terms"   className="block py-3 text-[#A1A1AA] hover:text-white transition-colors font-medium border-b border-white/5 text-sm">Terms &amp; Conditions</a>
              <a
                href="#download"
                onClick={() => setMenuOpen(false)}
                className="mt-4 block text-center bg-[#3B9EFF] text-black font-bold uppercase tracking-wider rounded-full px-6 py-3 hover:bg-white transition-colors"
              >
                Download App
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
